import dayjs from "dayjs";
import { toPeriodDateKey } from "./patientPeriodSummary";

export const displayId = (value) =>
  value == null || value === "" ? "—" : String(value);

export const resolveEntityId = (item, keys = []) => {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const value = item[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

export const toVisitScopeId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) return null;
    return toVisitScopeId(
      value.id ??
        value.appointmentId ??
        value.appointment_id ??
        value.prescriptionId ??
        value.prescription_id
    );
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) return String(Math.trunc(numeric));
  const text = String(value).trim();
  if (!text || text === "0" || text === "—" || text.toLowerCase() === "null") {
    return null;
  }
  return text;
};

const unwrapEntityId = (value) => {
  if (value != null && typeof value === "object") return toVisitScopeId(value);
  return value;
};

export const getAppointmentId = (item) =>
  unwrapEntityId(resolveEntityId(item, ["id", "appointmentId", "appointment_id"]));

export const getLinkedAppointmentId = (item) =>
  unwrapEntityId(
    resolveEntityId(item, ["appointmentId", "appointment_id"]) ??
      resolveEntityId(item?.appointment, ["id", "appointmentId", "appointment_id"])
  );

export const getLinkedPrescriptionId = (item) =>
  unwrapEntityId(resolveEntityId(item, ["prescriptionId", "prescription_id"]));

export const getPrescriptionRecordId = (item) =>
  unwrapEntityId(resolveEntityId(item, ["id", "prescriptionId", "prescription_id"]));

export const getAppointmentDateTimeValue = (item) =>
  item?.appointmentDateTime ||
  item?.appointment_date_time ||
  item?.appointmentDate ||
  item?.appointment_date ||
  item?.date ||
  item?.scheduledAt ||
  item?.scheduled_at ||
  null;

export const parseTimelineDateTime = (value) => {
  if (value == null || value === "") return null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const dt = dayjs(new Date(year, month - 1, day, hour, minute, second));
    return dt.isValid() ? dt : null;
  }

  if (value instanceof Date) {
    const dt = dayjs(value);
    return dt.isValid() ? dt : null;
  }

  const dt = dayjs(value);
  return dt.isValid() ? dt : null;
};

export const formatTimelineDateTime = (
  value,
  { withTime = true, fallback = "" } = {}
) => {
  const dt = parseTimelineDateTime(value);
  if (!dt) return fallback;
  return dt.format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
};

const timeFromAdmissionTime = (time) => {
  if (time == null || time === "") return { hour: 0, minute: 0, second: 0, hasTime: false };

  if (Array.isArray(time) && time.length >= 2) {
    return {
      hour: Number(time[0]) || 0,
      minute: Number(time[1]) || 0,
      second: Number(time[2]) || 0,
      hasTime: true,
    };
  }

  if (typeof time === "string") {
    if (time.includes("T")) {
      const parsed = dayjs(time);
      if (parsed.isValid()) {
        return {
          hour: parsed.hour(),
          minute: parsed.minute(),
          second: parsed.second(),
          hasTime: true,
        };
      }
    }
    const parts = time.split(":");
    if (parts.length >= 2) {
      return {
        hour: Number(parts[0]) || 0,
        minute: Number(parts[1]) || 0,
        second: Number(parts[2]) || 0,
        hasTime: true,
      };
    }
  }

  if (time instanceof Date) {
    return {
      hour: time.getHours(),
      minute: time.getMinutes(),
      second: time.getSeconds(),
      hasTime: true,
    };
  }

  return { hour: 0, minute: 0, second: 0, hasTime: false };
};

export const formatAdmissionDateTime = (admission, fallback = "") => {
  if (!admission) return fallback;

  const date = admission.admissionDate ?? admission.admission_date;
  const time = admission.admissionTime ?? admission.admission_time;
  const { hour, minute, second, hasTime } = timeFromAdmissionTime(time);

  if (Array.isArray(date) && date.length >= 3) {
    const dt = dayjs(
      new Date(date[0], date[1] - 1, date[2], hour, minute, second)
    );
    if (!dt.isValid()) return fallback;
    return dt.format(hasTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
  }

  if (typeof date === "string") {
    if (date.includes("T") && dayjs(date).isValid() && !hasTime) {
      return dayjs(date).format("DD MMM YYYY, hh:mm A");
    }
    const datePart = date.split("T")[0];
    if (hasTime) {
      const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
      const combined = dayjs(`${datePart}T${timeStr}`);
      if (combined.isValid()) return combined.format("DD MMM YYYY, hh:mm A");
    }
    const parsed = dayjs(datePart);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : fallback;
  }

  if (date instanceof Date || (date && dayjs(date).isValid())) {
    const dt = dayjs(date);
    if (!dt.isValid()) return fallback;
    if (hasTime) {
      return dt.hour(hour).minute(minute).second(second).format("DD MMM YYYY, hh:mm A");
    }
    return dt.format(dt.hour() || dt.minute() ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
  }

  return fallback;
};

export const resolveAdmissionPrescriptionId = (admission, prescriptions = []) => {
  const fromAdmission = getLinkedPrescriptionId(admission);
  if (fromAdmission != null) return fromAdmission;
  const first = Array.isArray(prescriptions) ? prescriptions[0] : prescriptions;
  return getPrescriptionRecordId(first);
};

export const idsEqual = (left, right) => {
  const leftId = toVisitScopeId(left);
  const rightId = toVisitScopeId(right);
  if (!leftId || !rightId) return false;
  return leftId === rightId;
};

export const getInvoiceAppointmentId = (invoice) =>
  toVisitScopeId(
    invoice?.appointmentId ??
      invoice?.appointment_id ??
      invoice?.appointment?.id ??
      invoice?.appointment?.appointmentId
  );

export const getInvoicePrescriptionId = (invoice) => {
  const direct = toVisitScopeId(
    invoice?.prescriptionId ??
      invoice?.prescription_id ??
      invoice?.opdPrescriptionId ??
      invoice?.opd_prescription_id ??
      invoice?.prescription?.id
  );
  if (direct) return direct;
  const items =
    invoice?.items ||
    invoice?.medicines ||
    invoice?.medicineDetails ||
    invoice?.labTests ||
    [];
  if (!Array.isArray(items)) return null;
  for (const item of items) {
    const fromItem = toVisitScopeId(
      item?.prescriptionId ?? item?.prescription_id
    );
    if (fromItem) return fromItem;
  }
  return null;
};

export const getInvoiceDateValue = (invoice) =>
  invoice?.date ??
  invoice?.invoiceDate ??
  invoice?.invoice_date ??
  invoice?.createdAt ??
  invoice?.created_at ??
  invoice?.createdDate ??
  null;

const toIdSet = (ids) =>
  new Set(
    (Array.isArray(ids) ? ids : ids != null ? [ids] : [])
      .map(toVisitScopeId)
      .filter(Boolean)
  );

/** True when a record timestamp falls in this visit's window (until the next same-day appointment). */
export const recordBelongsToVisitWindow = (
  recordTime,
  appointmentId,
  visits = []
) => {
  const scopeAppointment = toVisitScopeId(appointmentId);
  const sorted = (visits || [])
    .map((visit) => ({
      id: toVisitScopeId(visit?.id),
      ms: parseTimelineDateTime(visit?.dateTime)?.valueOf(),
    }))
    .filter((visit) => visit.id && visit.ms != null)
    .sort((a, b) => a.ms - b.ms || String(a.id).localeCompare(String(b.id)));

  if (!scopeAppointment || sorted.length === 0) return false;
  if (sorted.length === 1) {
    if (sorted[0].id !== scopeAppointment) return false;
    const recordDay = parseTimelineDateTime(recordTime);
    if (!recordDay) return false;
    return recordDay.format("YYYY-MM-DD") === dayjs(sorted[0].ms).format("YYYY-MM-DD");
  }

  const recordMs = parseTimelineDateTime(recordTime)?.valueOf();
  if (recordMs == null) return false;

  if (recordMs < sorted[0].ms) {
    return sorted[0].id === scopeAppointment;
  }

  for (let i = 0; i < sorted.length; i += 1) {
    const start = sorted[i].ms;
    let end = Number.POSITIVE_INFINITY;
    for (let j = i + 1; j < sorted.length; j += 1) {
      if (sorted[j].ms > start) {
        end = sorted[j].ms;
        break;
      }
    }
    const isLastWithThisStart =
      i === sorted.length - 1 || sorted[i + 1].ms > start;
    if (!isLastWithThisStart) continue;
    if (recordMs >= start && recordMs < end) {
      return sorted[i].id === scopeAppointment;
    }
  }
  return sorted[sorted.length - 1].id === scopeAppointment;
};

/**
 * True when an invoice belongs to one OPD visit (not the whole calendar day).
 * Prescription ID wins for medicine/pathology; then appointment ID;
 * unlinked invoices use the time window until the next appointment.
 */
export const invoiceMatchesVisit = (
  invoice,
  {
    dateKey,
    appointmentId,
    prescriptionIds = [],
    appointmentDateTime,
    siblingAppointments = [],
  } = {}
) => {
  if (!invoice) return false;

  const scopeAppointment = toVisitScopeId(appointmentId);
  const scopeRxSet = toIdSet(prescriptionIds);
  const invAppointment = getInvoiceAppointmentId(invoice);
  const invPrescription = getInvoicePrescriptionId(invoice);

  if (invPrescription && scopeRxSet.has(invPrescription)) {
    return true;
  }

  if (invPrescription) {
    const belongsToSibling = (siblingAppointments || []).some((visit) => {
      const visitId = toVisitScopeId(visit?.id);
      if (scopeAppointment && visitId === scopeAppointment) return false;
      return toIdSet(visit?.prescriptionIds).has(invPrescription);
    });
    if (belongsToSibling) return false;
  }

  if (scopeAppointment && invAppointment && invAppointment === scopeAppointment) {
    return true;
  }

  if (scopeAppointment && invAppointment && invAppointment !== scopeAppointment) {
    return false;
  }

  const invoiceTime = getInvoiceDateValue(invoice);
  const visits =
    Array.isArray(siblingAppointments) && siblingAppointments.length > 0
      ? siblingAppointments
      : [{ id: appointmentId, dateTime: appointmentDateTime }];

  if (recordBelongsToVisitWindow(invoiceTime, appointmentId, visits)) {
    return true;
  }

  const invoiceDateKey = toPeriodDateKey(invoiceTime);
  if (!dateKey || invoiceDateKey !== dateKey) return false;

  const sameDayVisits = (visits || []).filter((visit) => {
    const visitDate = toPeriodDateKey(visit?.dateTime);
    return visitDate === dateKey;
  });
  if (sameDayVisits.length > 1) return false;
  if (sameDayVisits.length === 1) {
    return toVisitScopeId(sameDayVisits[0]?.id) === scopeAppointment;
  }
  return Boolean(scopeAppointment && visits.length <= 1);
};

export const labIdentityKeys = (lab) => {
  if (!lab) return [];
  const keys = [];
  const reportId = lab.reportId ?? lab.report_id;
  if (reportId != null && reportId !== "") keys.push(`report:${reportId}`);
  const testName = String(lab.testName || lab.name || "").trim().toLowerCase();
  const rxId = toVisitScopeId(lab.prescriptionId ?? lab.prescription_id) || "";
  const dateKey =
    toPeriodDateKey(
      lab.reportDate || lab.report_date || lab.createdAt || lab.created_at
    ) || "";
  if (testName && rxId) keys.push(`test:${testName}|rx:${rxId}|date:${dateKey}`);
  if (testName && dateKey) keys.push(`test:${testName}|date:${dateKey}`);
  return keys;
};

export const labRecordKey = (lab) => {
  const keys = labIdentityKeys(lab);
  if (keys.length > 0) return keys[0];
  const rowId = lab?.id;
  if (
    rowId != null &&
    rowId !== "" &&
    String(rowId) !== String(lab?.labTestId ?? lab?.testId ?? "")
  ) {
    return `id:${rowId}`;
  }
  const testName = String(lab?.testName || lab?.name || "").trim().toLowerCase();
  return `name:${testName}`;
};

export const dedupeLabRecords = (labs = []) => {
  const seen = new Set();
  return (Array.isArray(labs) ? labs : []).filter((lab) => {
    if (!lab) return false;
    const keys = labIdentityKeys(lab);
    if (keys.length === 0) keys.push(labRecordKey(lab));
    if (keys.some((key) => seen.has(key))) return false;
    keys.filter(Boolean).forEach((key) => seen.add(key));
    return true;
  });
};

/** Path + location state for opening a patient's OPD/IPD timeline. */
export const buildPatientTimelineNavigation = ({
  user,
  patientId,
  isIpd = false,
  timelineTab = null,
} = {}) => {
  const id = encodeURIComponent(String(patientId ?? "").trim());
  const role = String(user?.roleName || user?.role || "")
    .trim()
    .toLowerCase();
  const type = isIpd ? "ipd" : "opd";
  const focusTab = String(timelineTab || "").trim().toLowerCase();
  const tabQuery = focusTab ? `&timelineTab=${encodeURIComponent(focusTab)}` : "";

  // Doctor/surgeon and pharmacy admin both land on /manage-patient — route the timeline
  // through it (instead of the bare /opd-dashboard or /ipd-dashboard page) so the patient's
  // full record still shows the surrounding Patients-page tabs (Patients In Queue / In
  // Patients / Out Patients), not just an isolated timeline view.
  if (role === "pharmacy admin" || role === "doctor" || role === "surgeon") {
    const outerTab = isIpd ? "2" : "3";
    return {
      path: `/manage-patient?patientId=${id}&type=${type}&tab=${outerTab}${tabQuery}`,
      state: focusTab ? { timelineTab: focusTab } : undefined,
    };
  }

  const base = isIpd ? `/ipd-dashboard/${id}` : `/opd-dashboard/${id}`;
  const path = focusTab
    ? `${base}?timelineTab=${encodeURIComponent(focusTab)}`
    : base;
  return {
    path,
    state: {
      tab: type,
      ...(focusTab ? { timelineTab: focusTab } : {}),
    },
  };
};

export const resolveTimelineFocusTab = ({
  propTab,
  locationState,
  searchParams,
} = {}) => {
  const raw =
    propTab ||
    locationState?.timelineTab ||
    (searchParams && typeof searchParams.get === "function"
      ? searchParams.get("timelineTab")
      : null) ||
    "";
  const tab = String(raw).trim().toLowerCase();
  if (tab === "appointments" || tab === "appointment") return "appointments";
  if (tab === "prescriptions" || tab === "prescription") return "prescriptions";
  return "";
};

export const getDefaultOpdTimelineTabIndex = () => {
  // Tabs are now: Prescriptions, Vital Signs, Lab Test Result, OPD Summary —
  // Patient Info and Appointments were removed (already shown in the per-visit
  // card header), so Prescriptions (index 0) is the default for everyone.
  return 0;
};

export const getDefaultIpdTimelineTabIndex = (
  timelineTab,
  { isPharmacyAdmin = false } = {}
) => {
  if (timelineTab === "appointments") return 0;
  if (timelineTab === "prescriptions") return 2;
  return isPharmacyAdmin ? 2 : 0;
};
